import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { toast } from 'sonner'
import { getErrorDescription } from '@/lib/error-utils'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { usePublicInquiry } from '@/stores/lead.store'
import { usePublicQuote, useSharePublicInquiryByEmail } from '@/stores/quotes.store'
import { getStoredToken, setToken } from '@/stores/token.store'
import { setPortalContext, setCustomerContext, captureError, trackLegacyPortalBootstrap } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { getInquiryOrgInfo } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetInquiryOrgInfoRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn, formatCents, quoteGrandTotalCents } from '@/lib/utils'
import { formatTimestamp, formatDateTimeRange } from '@/lib/date-utils'
import { QuoteDetail, QuoteSkeleton } from '@/components/quote-detail'
import { QuoteListItem } from '@/components/quote-list-item'
import { ErrorBoundary } from '@/components/error-boundary'
import { X, Loader2, Share2, Send, Calendar, MapPin, Users, FileQuestion } from 'lucide-react'
import { PortalHeader } from '@/components/portal-header'
import { PortalFooter } from '@/components/portal-footer'
import { resolveSelectedQuoteId } from '@/lib/selected-quote-id'
import { scrollIntoViewWhenStacked } from '@/lib/mobile-layout'

export const Route = createFileRoute('/lead/$id')({
  component: LeadDetailPage,
})

function LeadDetailPage() {
  const { id } = useParams({ from: '/lead/$id' })
  const navigate = useNavigate()
  const { inquiry, isLoading, hasLoaded, isError } = usePublicInquiry(id)
  const [isCompareMode, setIsCompareMode] = useState(false)
  const [userSelectedQuoteId, setUserSelectedQuoteId] = useState<string | null>(null)
  const [selectedQuoteIds, setSelectedQuoteIds] = useState<string[]>([])
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false)
  const [shareEmails, setShareEmails] = useState('')
  const { share: shareQuote, isPending: isSharePending } = useSharePublicInquiryByEmail()
  const [isTermsOpen, setIsTermsOpen] = useState(false)

  const selectedQuoteId = resolveSelectedQuoteId(userSelectedQuoteId, inquiry?.quotes)

  // Handle token extraction. Quote rendering is allowed to fall back to
  // anonymous backend access, so local expiry/claim checks are informational
  // here instead of hard gates.
  useEffect(() => {
    // Extract token from URL params
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('token')

    if (urlToken) {
      trackLegacyPortalBootstrap('lead')
      // Store token in localStorage with automatic expiry detection. The
      // transport will continue sending it on quote pages even if it is expired
      // or cannot be attributed locally.
      setToken(id, urlToken)

      // Clean up URL by removing token parameter
      navigate({
        to: '/lead/$id',
        params: { id },
        search: {},
        replace: true, // Replace history entry to avoid keeping token in browser history
      })
    }
  }, [id, navigate])

  // Set Sentry context when inquiry data loads
  useEffect(() => {
    if (inquiry) {
      setPortalContext({
        organizationId: inquiry.organization?.id,
        quoteId: selectedQuoteId || undefined,
      })
      setCustomerContext({
        name: inquiry.contactFirstName,
      })
    }
  }, [inquiry, selectedQuoteId])

  // Memoized handlers
  const handleToggleCompareMode = useCallback(() => {
    if (isCompareMode) {
      // Exit compare mode
      setIsCompareMode(false)
      setSelectedQuoteIds([])
      // Set selected quote to first one if available
      if (inquiry?.quotes && inquiry.quotes.length > 0) {
        setUserSelectedQuoteId(inquiry.quotes[0].id)
      }
    } else {
      // Enter compare mode
      setIsCompareMode(true)
      // If a quote is already selected in single mode, make it the first selected in compare mode
      if (selectedQuoteId) {
        setSelectedQuoteIds([selectedQuoteId])
      } else {
        setSelectedQuoteIds([])
      }
    }
  }, [isCompareMode, selectedQuoteId, inquiry?.quotes])

  const detailPanelRef = useRef<HTMLDivElement>(null)

  const handleQuoteClick = useCallback((quoteId: string) => {
    if (isCompareMode) {
      // Toggle selection in compare mode
      setSelectedQuoteIds(prev => {
        if (prev.includes(quoteId)) {
          // Deselect
          return prev.filter(id => id !== quoteId)
        } else if (prev.length < 2) {
          // Select (max 2)
          return [...prev, quoteId]
        }
        // If trying to select 3rd, do nothing
        return prev
      })
    } else {
      // Normal single selection. On phones the detail panel sits below the list,
      // so bring it into view or the tap looks like it did nothing.
      setUserSelectedQuoteId(quoteId)
      scrollIntoViewWhenStacked(detailPanelRef.current)
    }
  }, [isCompareMode])

  const handleExitComparison = useCallback(() => {
    setIsCompareMode(false)
    setSelectedQuoteIds([])
    if (inquiry?.quotes && inquiry.quotes.length > 0) {
      setUserSelectedQuoteId(inquiry.quotes[0].id)
    }
  }, [inquiry?.quotes])

  const handleOpenTerms = useCallback(() => {
    setIsTermsOpen(true)
  }, [])

  const handleShareSubmit = useCallback(async () => {
    const emails = shareEmails.split(',').map(e => e.trim()).filter(Boolean)
    if (emails.length === 0) return

    try {
      const result = await shareQuote(id, emails)
      toast.success(`Quote sent to ${result.emailsQueued} recipient${result.emailsQueued !== 1 ? 's' : ''}`)
      setIsShareDialogOpen(false)
      setShareEmails('')
    } catch (error) {
      toast.error('Failed to send quote. Please try again.', {
        description: getErrorDescription(error),
      })
      console.error('Share quote error:', error)
      reportError(error, { feature: 'portal_share_quote', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), { context: 'lead-detail', action: 'share-quote' })
    }
  }, [id, shareEmails, shareQuote])

  // Show skeleton while loading or before first load
  if (isLoading || !hasLoaded) {
    return <LeadDetailSkeleton />
  }

  // Show error state
  if (isError || !inquiry) {
    return <LeadNoAccessState resourceId={id} />
  }

  // Check if terms and conditions exist
  const termsAgreement = inquiry.organization?.agreements?.find(
    (a) => a.type === 'terms_and_conditions'
  )
  const hasTerms = !!(termsAgreement?.clauses && termsAgreement.clauses.length > 0)
  const customerName = [inquiry.contactFirstName, inquiry.contactLastName].filter(Boolean).join(' ')
  // A one-item picker is just a duplicate header above the quote on phones.
  const hasSingleQuote = inquiry.quotes?.length === 1

  return (
    // Bottom padding keeps the footer clear of QuoteDetail's fixed mobile action bar.
    <div className={cn('min-h-svh flex flex-col bg-background', !isCompareMode && 'pb-safe-20 lg:pb-0')}>
      <PortalHeader
        orgName={inquiry.organization?.name || 'Quote'}
        logoUrl={inquiry.organization?.logoUrl}
        contactEmail={inquiry.organization?.contactEmail}
        hasGallery={!!inquiry.organization?.galleryImages?.length}
        secondary={customerName ? `Quote for ${customerName}` : 'Quote'}
        actions={(
          <Button size="sm" aria-label="Share quote" onClick={() => setIsShareDialogOpen(true)}>
            <Share2 className="h-4 w-4" />
            <span className="hidden sm:inline">Share Quote</span>
          </Button>
        )}
      />

      {/* Main Content - Split Layout */}
      <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:grid-rows-[auto_1fr] lg:items-start lg:py-8">
          {/* DOM order is the phone reading order — quote picker (only useful with 2+
              quotes), then the quote, then the customer's own inquiry details — so focus and
              screen-reader order match what's on screen. From lg up, explicit grid placement
              puts inquiry + picker in the left rail and the quote on the right; the 1fr second
              row absorbs the quote's extra height so the rail stays packed at the top. */}
          {/* Quotes List */}
          <Card className={cn('lg:col-start-1 lg:row-start-2', hasSingleQuote && 'hidden lg:flex')}>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Quotes</CardTitle>
                  <CardDescription>
                    {inquiry.quotes && inquiry.quotes.length > 0
                      ? `${inquiry.quotes.length} quote(s) available`
                      : 'No quotes available'}
                  </CardDescription>
                </div>
                {inquiry.quotes && inquiry.quotes.length > 1 && (
                  <Button
                    variant={isCompareMode ? "default" : "outline"}
                    size="sm"
                    onClick={handleToggleCompareMode}
                  >
                    {isCompareMode ? "Exit Compare" : "Compare Quotes"}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {inquiry.quotes && inquiry.quotes.length > 0 ? (
                <div className="space-y-2">
                  {inquiry.quotes.map((lightweightQuote) => (
                    <QuoteListItem
                      key={lightweightQuote.id}
                      quoteId={lightweightQuote.id}
                      isSelected={!isCompareMode && selectedQuoteId === lightweightQuote.id}
                      isInComparison={selectedQuoteIds.includes(lightweightQuote.id)}
                      isCompareMode={isCompareMode}
                      onClick={() => handleQuoteClick(lightweightQuote.id)}
                    />
                  ))}
                </div>
              ) : (
                <Empty>
                  <EmptyHeader>
                    <EmptyTitle>No quotes yet</EmptyTitle>
                    <EmptyDescription>
                      Your quotes will appear here once they're created.
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )}
            </CardContent>
          </Card>


          {/* Right Panel - Selected Quote Details or Comparison */}
          {!isCompareMode && (
            <div ref={detailPanelRef} className="scroll-mt-4 md:scroll-mt-24 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:sticky lg:top-24 lg:h-fit">
              {selectedQuoteId ? (
                <ErrorBoundary>
                  <QuoteDetail
                    quoteId={selectedQuoteId}
                    inquiryId={id}
                    compact={false}
                    hasTerms={hasTerms}
                    onOpenTerms={handleOpenTerms}
                    orgName={inquiry.organization?.name}
                    customerName={customerName || undefined}
                  />
                </ErrorBoundary>
              ) : (
                <Card>
                  <CardContent className="py-12">
                    <Empty>
                      <EmptyHeader>
                        <EmptyTitle>No quote selected</EmptyTitle>
                        <EmptyDescription>
                          Select a quote from the list to view its details.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Comparison View - Show when in compare mode */}
          {isCompareMode && (
            <div className="space-y-4 lg:col-start-2 lg:row-span-2 lg:row-start-1">
              {/* Comparison Toolbar */}
              {selectedQuoteIds.length === 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div>
                    <h3 className="font-semibold text-sm">Compare Quotes</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Select two quotes from your list to compare
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExitComparison}>
                    <X className="h-4 w-4 mr-2" />
                    Exit Comparison
                  </Button>
                </div>
              ) : selectedQuoteIds.length === 1 ? (
                <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div>
                    <h3 className="font-semibold text-sm">Compare Quotes</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Select another quote to compare
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleExitComparison}>
                    <X className="h-4 w-4 mr-2" />
                    Exit Comparison
                  </Button>
                </div>
              ) : (
                <ComparisonToolbarWrapper
                  quoteId1={selectedQuoteIds[0]}
                  quoteId2={selectedQuoteIds[1]}
                  onExit={handleExitComparison}
                />
              )}

              {/* Two Quote Columns */}
              {selectedQuoteIds.length > 0 && (
                <div className="grid gap-6 lg:grid-cols-2">
                  <div className="lg:sticky lg:top-24 lg:h-fit">
                    <ErrorBoundary>
                      <QuoteDetail
                        quoteId={selectedQuoteIds[0]}
                        inquiryId={id}
                        compact={true}
                        hasTerms={hasTerms}
                        onOpenTerms={handleOpenTerms}
                        orgName={inquiry.organization?.name}
                        customerName={customerName || undefined}
                      />
                    </ErrorBoundary>
                  </div>
                  <div className="lg:sticky lg:top-24 lg:h-fit">
                    {selectedQuoteIds.length === 2 ? (
                      <ErrorBoundary>
                        <QuoteDetail
                          quoteId={selectedQuoteIds[1]}
                          inquiryId={id}
                          compact={true}
                          hasTerms={hasTerms}
                          onOpenTerms={handleOpenTerms}
                          orgName={inquiry.organization?.name}
                          customerName={customerName || undefined}
                        />
                      </ErrorBoundary>
                    ) : (
                      <QuoteSkeleton />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Inquiry Details Card with Tabs */}
          <div className="lg:col-start-1 lg:row-start-1">
            <InquiryDetailsCard inquiryId={id} inquiry={inquiry} events={inquiry.events} />
          </div>
      </div>

      {/* Share Quote Dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share Quote</DialogTitle>
            <DialogDescription>
              Send the quote by email for tracked recipient access. Forwarded or anonymous views are not attributed in the activity feed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Email Section */}
            <div className="space-y-2">
              <Label htmlFor="share-emails">Send via Email</Label>
              <Textarea
                id="share-emails"
                placeholder="Enter email addresses, separated by commas"
                value={shareEmails}
                onChange={(e) => setShareEmails(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleShareSubmit}
              disabled={!shareEmails.trim() || isSharePending}
            >
              {isSharePending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {isSharePending ? 'Sending...' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer with Terms & Conditions */}
      {inquiry.organization && (
        <PortalFooter
          organization={inquiry.organization}
          isTermsOpen={isTermsOpen}
          onTermsOpenChange={setIsTermsOpen}
        />
      )}
    </div>
  )
}

function InquiryDetailsCard({
  inquiry,
  events,
}: {
  inquiryId: string
  inquiry: {
    createdAt?: { seconds?: bigint | number }
    items?: Array<{ id: string; title: string }>
  }
  events?: Array<{
    id: string
    eventDate?: { seconds?: bigint | number }
    eventLength?: number
    eventGuests?: number
    eventLocation?: string
    eventTimezone?: string
  }>
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Created At */}
          {inquiry.createdAt && (
            <div>
              <p className="text-sm text-muted-foreground">Submitted At</p>
              <p className="text-sm font-medium">
                {formatTimestamp(inquiry.createdAt, 'MMM dd, yyyy h:mm a')}
              </p>
            </div>
          )}

          {/* Event Details */}
          {events && events.length > 0 && (
            <>
              <Separator />
              <div className="space-y-4">
                {events.map((event, index) => (
                  <div key={event.id || index} className="space-y-3">
                    {events.length > 1 && (
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Event {index + 1}
                      </p>
                    )}
                    {event.eventDate && (
                      <div className="flex items-start gap-3">
                        <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">
                            {formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone)}
                          </p>
                        </div>
                      </div>
                    )}
                    {event.eventLocation && (
                      <div className="flex items-start gap-3">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">{event.eventLocation}</p>
                        </div>
                      </div>
                    )}
                    {event.eventGuests !== undefined && event.eventGuests > 0 && (
                      <div className="flex items-start gap-3">
                        <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-muted-foreground">{event.eventGuests} guests</p>
                        </div>
                      </div>
                    )}
                    {index < events.length - 1 && <Separator className="my-2" />}
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add Ons Selected */}
          {inquiry.items && inquiry.items.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Add Ons Selected</p>
              <div className="flex flex-wrap gap-2">
                {inquiry.items.map((item) => (
                  <Badge key={item.id} variant="default">
                    {item.title}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ComparisonToolbarWrapper({
  quoteId1,
  quoteId2,
  onExit,
}: {
  quoteId1: string
  quoteId2: string
  onExit: () => void
}) {
  const { quote: quote1, isLoading: isLoading1, hasLoaded: hasLoaded1 } = usePublicQuote(quoteId1)
  const { quote: quote2, isLoading: isLoading2, hasLoaded: hasLoaded2 } = usePublicQuote(quoteId2)

  const isLoading = isLoading1 || isLoading2
  const hasLoaded = hasLoaded1 && hasLoaded2

  // Show skeleton while loading
  if (isLoading || !hasLoaded) {
    return (
      <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
        <Skeleton className="h-12 w-full" />
      </div>
    )
  }

  // Don't render if quotes not found
  if (!quote1 || !quote2) {
    return null
  }

  return <ComparisonToolbar quote1={quote1} quote2={quote2} onExit={onExit} />
}

function ComparisonToolbar({
  quote1,
  quote2,
  onExit,
}: {
  quote1: { quoteNumber: number; totalCents: bigint; enforcedTipCents?: bigint } | undefined
  quote2: { quoteNumber: number; totalCents: bigint; enforcedTipCents?: bigint } | undefined
  onExit: () => void
}) {
  if (!quote1 || !quote2) return null

  const difference = Number(quoteGrandTotalCents(quote1)) - Number(quoteGrandTotalCents(quote2))
  const absDifference = Math.abs(difference)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-primary/5 rounded-lg border border-primary/20">
      <div>
        <h3 className="font-semibold text-sm">
          Comparing: Quote #{quote1.quoteNumber} vs Quote #{quote2.quoteNumber}
        </h3>
        {difference !== 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            Difference: {formatCents(absDifference)}{' '}
            {difference < 0 ? '(Quote #' + quote2.quoteNumber + ' is cheaper)' : '(Quote #' + quote1.quoteNumber + ' is cheaper)'}
          </p>
        )}
      </div>
      <Button variant="outline" size="sm" onClick={onExit}>
        <X className="h-4 w-4 mr-2" />
        Exit Comparison
      </Button>
    </div>
  )
}

function LeadNoAccessState({ resourceId }: { resourceId: string }) {
  const token = getStoredToken(resourceId) || ''
  const request = useMemo(
    () => create(GetInquiryOrgInfoRequestSchema, { token }),
    [token],
  )
  const { data: orgInfo } = useQuery(getInquiryOrgInfo, request, {
    enabled: !!token,
  })

  const orgName = orgInfo?.orgName
  const orgLogoUrl = orgInfo?.orgLogoUrl

  return (
    <div className="min-h-svh bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-4">
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt={orgName || 'Organization'}
              className="h-20 w-20 mx-auto rounded-lg border border-border object-cover"
            />
          ) : (
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
              <FileQuestion className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          {orgName && (
            <div>
              <h1 className="display-heading text-3xl font-semibold tracking-tight text-foreground">
                {orgName}
              </h1>
            </div>
          )}
        </div>

        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileQuestion className="h-6 w-6 text-amber-600" />
              <div>
                <CardTitle className="text-amber-800">You don't have access to this quote</CardTitle>
                <CardDescription>We couldn't load the requested document</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {orgName
                ? <>Please reply to your email or contact <strong>{orgName}</strong> and ask them to send you a new link.</>
                : 'Please reply to your email or contact the business that sent it and ask them to send you a new link.'}
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-dim">
          {orgName
            ? `Need help? Contact ${orgName} directly.`
            : 'Need help? Contact the business that sent you this link.'}
        </p>
      </div>
    </div>
  )
}

function LeadDetailSkeleton() {
  return (
    <div className="min-h-svh">
      {/* Header Skeleton */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-28" />
            </div>
          </div>
          <Skeleton className="h-9 w-36" />
        </div>
      </header>

      {/* Content Skeleton */}
      <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 md:px-8 lg:grid-cols-[minmax(280px,360px)_minmax(0,1fr)] lg:py-8">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-56 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
